import { inject, Injectable, Signal } from '@angular/core';
import { splitParams } from '@/_utils/urls/split-params';
import { catchError, EMPTY, filter, map, Observable, take } from 'rxjs';
import { OffersApiService } from '@/_api/offers.api.service';
import { OfferList, Selected } from '@/_store/offers/offers.types';
import { Params, Router } from '@angular/router';
import { removeLastChar } from '@/_utils/strings/remove-last-char';
import { toObservable } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { CurrentOfferFacade } from '@/_store/current-offer/current-offer.facade';
import { MobileDialogService } from '@/common/mobile-dialog/mobile-dialog.service';
import { TransportKind } from '@/offer/offer.model';
import { Result } from '@/common/data.service';
import { OffersFacade } from '@/_store/offers/offers.facade';
import { getOfferUrl } from '@/_utils/urls/get-offer-url';

@Injectable({
	providedIn: 'root',
})
export class MainService {
	private readonly offersApiService = inject(OffersApiService);
	private readonly currentOfferFacade = inject(CurrentOfferFacade);
	private readonly offersFacade = inject(OffersFacade);
	private readonly mobileDialogService = inject(MobileDialogService);
	private readonly router = inject(Router);

	readonly fetchOfferListFromQuery$ = (querySignal: Signal<string>): Observable<OfferList[]> =>
		toObservable(querySignal).pipe(
			filter((query) => !!query),
			take(1),
			switchMap((query) => this.loadOffersByQuery(query)),
		);

	getUrlParams(item: OfferList, target: 'url' | 'object', selected: Selected): string | Params {
		const params = {
			boards: item.board.id,
			promotion: item.promotion ? item.promotion.id : 0,
			room_type: item.roomType.id ?? '',
			stop_id: selected?.stops?.length ? selected.stops[0] : null,
			date_range: `${item.dateRange.lower},${item.dateRange.upper}`,
			own_transport: item.transport.kind === TransportKind.OWN_TRANSPORT,
			adults: item.roomType.adults,
			children: item.roomType.children,
			products: item.product.id,
			nights: item.term.nightStay,
		};

		let url = '';
		for (const [key, value] of Object.entries(params)) {
			url = url.concat(`${key}=${value}&`);
		}

		url = removeLastChar(url);

		return target === 'url' ? url : params;
	}

	handleButtonClick(event: Event, offer: OfferList): void {
		event.preventDefault();
		event.stopPropagation();

		this.currentOfferFacade.clearCurrentOffer();

		this.goToOffer(offer);
	}

	getOfferUrl(offer: OfferList): string {
		return getOfferUrl(offer);
	}

	private loadOffersByQuery(query: string): Observable<OfferList[]> {
		return this.offersApiService
			.getOffers('?' + splitParams(query) + '&limit=10', Intl.DateTimeFormat().resolvedOptions().timeZone)
			.pipe(
				map((result) => this.filterOffersByQuery(query, result)),
				catchError((error) => {
					console.error('Error fetching offers:', error);
					return EMPTY;
				}),
			);
	}

	private filterOffersByQuery(query: string, result: Result<OfferList>): OfferList[] {
		if (!query.includes('?products=')) {
			return result.results;
		} else {
			const products = query
				.split('&')
				.find((q) => q.includes('products'))
				.replace('?products=', '')
				.split(',')
				.map(Number);

			const arr = [];
			for (const product of products) {
				const foundProduct = result.results.find((o: OfferList) => o.product.id === product);
				if (foundProduct) {
					arr.push(foundProduct);
				}
			}
			return arr;
		}
	}

	private goToOffer(offer: OfferList): void {
		this.offersFacade.selected$.pipe(take(1)).subscribe((selected) => {
			const params: Params = this.getUrlParams(offer, 'object', selected) as Params;

			this.router.navigate([this.getOfferUrl(offer)], {
				queryParams: Object.assign({}, params),
			});

			this.mobileDialogService.close();
		});
	}
}
